// Every synced thing in the app is an entity: a small JSON object with an id and a kind.
// Entities live in memory (decrypted), in IndexedDB (encrypted) and on the server (encrypted).
// Large content (note text, journal pages, chapters) is a separate "body", loaded on demand.
//
// Modules may add optional fields to their own entity types; keep existing fields stable.

export interface BaseEntity {
  id: string;
  kind: Kind;
  createdAt: number;
  updatedAt: number;
  deleted?: boolean;
  /** set when the entity belongs to a password-protected vault (encrypted with the vault key) */
  vaultId?: string | null;
  /** never leaves this device (e.g. chats the user chose not to sync) */
  localOnly?: boolean;
}

export const KINDS = [
  'setting',
  'vault',
  'file',
  'food',
  'foodItem',
  'measurement',
  'sleep',
  'day',
  'workout',
] as const;
export type Kind = (typeof KINDS)[number];

// ------------------------------------------------------------------ settings & vaults

/** Synced, encrypted key/value settings. id = setting key. */
export interface Setting extends BaseEntity {
  kind: 'setting';
  value: unknown;
}

/**
 * A password-protected area. Its key is random and wrapped with a key derived from the
 * vault password; the vault entity itself is encrypted with the account data key.
 */
export interface Vault extends BaseEntity {
  kind: 'vault';
  name: string;
  salt: string;
  iterations: number;
  wrappedKey: string;
  hint?: string;
  /** minutes of inactivity before it locks again (0 = when the app locks) */
  autoLockMin?: number;
  /** unlocked with a numeric PIN (a PIN-locked notebook) instead of a password */
  pin?: boolean;
  /** place in vault lists (dragged); unset = after ordered ones, by name */
  order?: number;
}

// ------------------------------------------------------------------ files

/** An uploaded file (a food photo). Bytes are encrypted separately (core/files). */
export interface FileRef extends BaseEntity {
  kind: 'file';
  name: string;
  mime: string;
  size: number;
  /** owner, e.g. a food entry id */
  parentId?: string | null;
  width?: number;
  height?: number;
}

// ------------------------------------------------------------------ food
//
// Amounts on a FoodEntry are for what was eaten (quantity already applied). A FoodItem (the
// library: favorites and everything logged before) holds one serving; logging it multiplies.

export type Meal = 'breakfast' | 'lunch' | 'dinner' | 'snack';

export interface Nutrition {
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber?: number;
  sugar?: number;
  sodiumMg?: number;
  /** micronutrients keyed with their unit, e.g. vitamin_c_mg, folate_mcg, omega3_g */
  nutrients?: Record<string, number>;
}

export interface FoodEntry extends BaseEntity, Nutrition {
  kind: 'food';
  /** the calendar day it was eaten on, on the clock where the user was */
  day: string;
  /** epoch ms */
  at: number;
  /** IANA zone the user was in when they ate (for showing the time as it was) */
  tz?: string;
  meal: Meal;
  name: string;
  serving?: string;
  quantity: number;
  grams?: number;
  source: 'manual' | 'ai' | 'photo' | 'library' | 'import';
  /** the library item it was logged from */
  itemId?: string;
  photoId?: string;
  note?: string;
}

export interface FoodItem extends BaseEntity, Nutrition {
  kind: 'foodItem';
  name: string;
  serving?: string;
  grams?: number;
  /** usual meal, for suggestions */
  meal?: Meal;
  uses: number;
  lastUsedAt?: number;
  /** shown first in quick add */
  favorite?: boolean;
  supplement?: boolean;
  order?: number;
}

// ------------------------------------------------------------------ body

export interface Measurement extends BaseEntity {
  kind: 'measurement';
  /** epoch ms */
  at: number;
  /** the calendar day where the user was */
  day: string;
  tz?: string;
  weightKg: number;
  bodyFatPct?: number;
  muscleKg?: number;
  musclePct?: number;
  skeletalMusclePct?: number;
  waterPct?: number;
  boneKg?: number;
  proteinPct?: number;
  visceralFat?: number;
  subcutaneousFatPct?: number;
  bmr?: number;
  metabolicAge?: number;
  leanMassKg?: number;
  bmi?: number;
  heartRate?: number;
  source: 'manual' | 'renpho' | 'import';
  note?: string;
}

// ------------------------------------------------------------------ sleep & days
//
// Sleep sessions keep real instants (epoch ms) plus the clock offset Oura saw at each end.
// Which zone a night is shown in is worked out later (modules/sleep/zones.ts): a manual pick,
// else where Trace saw the user, else Oura's offset. Durations are always real elapsed time.

export interface SleepSession extends BaseEntity {
  kind: 'sleep';
  /** the morning it belongs to (Oura's "day") */
  day: string;
  /** long_sleep, sleep, late_nap, rest */
  type: string;
  /** epoch ms */
  start: number;
  end: number;
  /** minutes east of UTC that Oura's clock showed at bedtime / wake-up */
  startOffset?: number;
  endOffset?: number;
  /** IANA zone chosen by the user for this night */
  zone?: string;
  totalS?: number;
  inBedS?: number;
  awakeS?: number;
  remS?: number;
  deepS?: number;
  lightS?: number;
  latencyS?: number;
  efficiency?: number;
  hrAvg?: number;
  hrLow?: number;
  hrv?: number;
  breath?: number;
  /** 5-minute hypnogram: 1 deep, 2 light, 3 REM, 4 awake */
  phases?: string;
  source: 'oura' | 'import' | 'manual';
}

/** One calendar day of ring data (scores, steps, calories). id = `day-YYYY-MM-DD`. */
export interface DaySummary extends BaseEntity {
  kind: 'day';
  day: string;
  sleepScore?: number;
  readiness?: number;
  tempDev?: number;
  steps?: number;
  activeKcal?: number;
  totalKcal?: number;
  activityScore?: number;
  walkM?: number;
  /** imported days carry these instead of separate sessions when nothing better exists */
  restingHr?: number;
  hrv?: number;
}

/** A workout from Strava (imported from SmartCalorie). Trace movement is a separate cache. */
export interface Workout extends BaseEntity {
  kind: 'workout';
  /** epoch ms */
  start: number;
  durationS: number;
  distanceM?: number;
  /** Walk, Run, Ride, Strength training… (see activity/workouts.ts) */
  type: string;
  kcal?: number;
  /** easy | moderate | hard */
  intensity?: string;
  note?: string;
  source: 'strava' | 'import' | 'manual' | 'oura';
}

export type AnyEntity =
  | Setting
  | Vault
  | FileRef
  | FoodEntry
  | FoodItem
  | Measurement
  | SleepSession
  | DaySummary
  | Workout;

export type EntityOf<K extends Kind> = Extract<AnyEntity, { kind: K }>;

/** Kinds whose content lives in a separate body. */
export const BODY_KINDS: ReadonlySet<Kind> = new Set<Kind>([]);
